import { useState, Suspense, useLayoutEffect, useEffect } from "react";
import { createPortal } from "react-dom";
import { Trans, useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation, Link, Outlet, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { canPostEvents, roleLabels, type OrganizationRole, type OrgPermission } from "@/lib/api-producer";
import { ArrowLeft, LayoutDashboard, Ticket, Users, ScanLine, DollarSign, Mail, Tag, Globe, MapPin, Calendar, Megaphone, FileText, ClipboardList, Package, Settings, Award, Video, RefreshCcw, AlertCircle, Star, Lock, ExternalLink, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import ProducerEventForm from "@/pages/producer/ProducerEventForm";
import { getEventStatusStyle } from "@/components/EventStatusBadge";
import { scrollPageToTop } from "@/lib/scroll";
import { formatEventDateTimeRange } from "@/lib/event-timezone";
import { getEventPublicPath } from "@/lib/urls";
import { useEventFocusSlot } from "@/contexts/EventFocusSlotContext";
import { useSidebar } from "@/components/ui/sidebar";
import { ReviewsFeatureAnnouncement, useReviewsAnnouncement } from "@/components/ReviewsFeatureAnnouncement";

interface ProducerEventPanelProps {
  eventsBasePath?: string;
  listPath?: string;
  panelSlug?: string;
}

const PT_TAB_PATH_MAP: Record<string, string> = {
  "": "",
  "/tickets": "/ingressos",
  "/orders": "/pedidos",
  "/participants": "/participantes",
  "/guestlist": "/lista-convidados",
  "/checkin": "/checkin",
  "/financial": "/financeiro",
  "/messages": "/mensagens",
  "/coupons": "/cupons",
  "/promoters": "/promotores",
  "/team": "/equipe",
  "/certificates": "/certificados",
  "/streaming": "/transmissao",
  "/resales": "/revenda",
  "/reviews": "/avaliacoes",
  "/schedule": "/programacao",
};

const PT_TAB_PATH_REVERSE: Record<string, string> = Object.fromEntries(
  Object.entries(PT_TAB_PATH_MAP).map(([k, v]) => [v, k])
);

// labelKey: chave i18n (namespace "producerDashboard") resolvida no render via t(item.labelKey).
// hint: texto descritivo interno, hoje não renderizado em nenhuma UI (dead prop) — mantido em pt-BR.
const EVENT_OPERATIONS = [
  { key: "dashboard", labelKey: "eventPanel.nav.operations.dashboard", icon: LayoutDashboard, path: "", hint: "Visão geral" },
  { key: "tickets", labelKey: "eventPanel.nav.operations.tickets", icon: Ticket, path: "/tickets", hint: "Lotes e disponibilidade" },
  { key: "orders", labelKey: "eventPanel.nav.operations.orders", icon: DollarSign, path: "/orders", hint: "Transações" },
  { key: "participants", labelKey: "eventPanel.nav.operations.participants", icon: Users, path: "/participants", hint: "Base de compradores" },
  { key: "guestlist", labelKey: "eventPanel.nav.operations.guestlist", icon: Users, path: "/guestlist", hint: "Convidados" },
  { key: "checkin", labelKey: "eventPanel.nav.operations.checkin", icon: ScanLine, path: "/checkin", hint: "Acesso no evento" },
  { key: "certificates", labelKey: "eventPanel.nav.operations.certificates", icon: Award, path: "/certificates", hint: "Certificados de participação" },
  { key: "streaming", labelKey: "eventPanel.nav.operations.streaming", icon: Video, path: "/streaming", hint: "Link ao vivo" },
  { key: "team", labelKey: "eventPanel.nav.operations.team", icon: Users, path: "/team", hint: "Co-produtores e equipe" },
  { key: "financial", labelKey: "eventPanel.nav.operations.financial", icon: DollarSign, path: "/financial", hint: "Receita e repasses" },
  { key: "promoters", labelKey: "eventPanel.nav.operations.promoters", icon: Megaphone, path: "/promoters", hint: "Afiliados" },
  { key: "messages", labelKey: "eventPanel.nav.operations.messages", icon: Send, path: "/messages", hint: "Envio de e-mails e atendimento" },
  { key: "coupons", labelKey: "eventPanel.nav.operations.coupons", icon: Tag, path: "/coupons", hint: "Descontos" },
  { key: "resales", labelKey: "eventPanel.nav.operations.resales", icon: RefreshCcw, path: "/resales", hint: "Revenda de ingressos" },
  { key: "reviews", labelKey: "eventPanel.nav.operations.reviews", icon: Star, path: "/reviews", hint: "Avaliações dos participantes" },
  { key: "schedule", labelKey: "eventPanel.nav.operations.schedule", icon: Calendar, path: "/schedule", hint: "Programação do evento" },
] as const;

const EDIT_SECTIONS = [
  { key: "info", labelKey: "eventPanel.nav.editSections.info", icon: FileText, hint: "Identidade do evento" },
  { key: "venue", labelKey: "eventPanel.nav.editSections.venue", icon: MapPin, hint: "Online ou presencial" },
  { key: "form", labelKey: "eventPanel.nav.editSections.form", icon: ClipboardList, hint: "Perguntas de checkout" },
  { key: "products", labelKey: "eventPanel.nav.editSections.products", icon: Package, hint: "Upsell e catálogo" },
  { key: "settings", labelKey: "eventPanel.nav.editSections.settings", icon: Settings, hint: "Recursos avançados" },
] as const;

// Which org roles can access each tab. null = all active members (incl. staff).
const TAB_ORG_ROLES: Record<string, OrganizationRole[] | null> = {
  dashboard:    null,
  tickets:      ["owner", "admin", "editor"],
  orders:       ["owner", "admin", "editor"],
  participants: ["owner", "admin", "editor"],
  guestlist:    ["owner", "admin", "editor"],
  checkin:      null,
  certificates: ["owner", "admin", "editor"],
  streaming:    ["owner", "admin", "editor"],
  team:         ["owner", "admin"],
  financial:    ["owner", "admin"],
  promoters:    ["owner", "admin", "editor"],
  messages:     ["owner", "admin", "editor"],
  coupons:      ["owner", "admin", "editor"],
  resales:      ["owner", "admin", "editor"],
  reviews:      ["owner", "admin", "editor"],
  schedule:     ["owner", "admin", "editor"],
};

export default function ProducerEventPanel({ eventsBasePath = "/produtor/eventos", listPath = "/produtor/eventos", panelSlug = "painel" }: ProducerEventPanelProps) {
  const { t } = useTranslation("producerDashboard");
  const { id } = useParams();
  const { profile, role } = useAuth();
  const isPlatformAdmin = role === "admin";
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  // Slot da barra de foco no layout (produtor/admin). Quando presente, a nav do
  // evento é portada para a sidebar principal e o "modo foco" fica ativo.
  const { slot: navSlot } = useEventFocusSlot();
  const { isMobile, setOpenMobile } = useSidebar();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavAction, setPendingNavAction] = useState<(() => void) | null>(null);
  const { show: showReviewsAnnouncement, dismiss: dismissReviewsAnnouncement } = useReviewsAnnouncement(id);

  const queryClient = useQueryClient();
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);

  const publishMutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .update({ status: "published" })
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["producer-event-panel", id] });
      queryClient.invalidateQueries({ queryKey: ["producer-events"] });
      queryClient.invalidateQueries({ queryKey: ["edit-event", id] });
      toast({ title: t("eventPanel.toast.publishSuccessTitle"), description: t("eventPanel.toast.publishSuccessDescription") });
    },
    onError: (err: any) => {
      toast({ title: t("eventPanel.toast.publishErrorTitle"), description: err.message, variant: "destructive" });
    }
  });
  const editorStep = searchParams.get("editorStep");
  const isInlineEditing = !!editorStep;
  const currentEditStepIndex = EDIT_SECTIONS.findIndex((section) => section.key === editorStep);

  const { data: event, isLoading } = useQuery({
    queryKey: ["producer-event-panel", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, title, description, slug, start_date, end_date, is_online, venue_city, venue_name, status, visibility, cover_image_url, organization_id, organizations(slug)")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
    staleTime: 30_000,
  });

  const isPtPanel = panelSlug === "painel";
  const basePath = `${eventsBasePath}/${id}/${panelSlug}`;
  const rawSuffix = location.pathname.replace(basePath, "");
  const normalizedSuffix = rawSuffix.startsWith("/programacao") ? rawSuffix.replace("/programacao", "/schedule") : rawSuffix;
  const currentSuffix = isPtPanel ? (PT_TAB_PATH_REVERSE[normalizedSuffix] ?? normalizedSuffix) : normalizedSuffix;
  const activeTab = EVENT_OPERATIONS.find((t) => t.path === currentSuffix)
    || EVENT_OPERATIONS.find((t) => t.path !== "" && currentSuffix.startsWith(t.path))
    || EVENT_OPERATIONS[0];
  const { organizations } = useOrganization();
  const isOrg = !!(event as any)?.organization_id && !!(event as any)?.organizations?.slug;
  const orgSlug = isOrg ? (event as any)?.organizations?.slug : null;
  const eventPublicPath = event?.slug ? getEventPublicPath(event.slug, orgSlug || profile?.organizer_slug, isOrg) : null;
  const eventTimeZone = "America/Sao_Paulo"; // Fallback to current default since column is missing

  const myOrgMembership = isOrg && organizations
    ? organizations.find((o) => o.org_id === (event as any)?.organization_id && o.member_status === "active")
    : undefined;
  const myOrgRole = myOrgMembership?.org_role as OrganizationRole | undefined;
  const myCustomPerms = (myOrgMembership?.custom_permissions ?? null) as OrgPermission[] | null;

  const PERMISSION_TAB_MAP: Record<string, OrgPermission | null> = {
    dashboard:    null,
    tickets:      "edit_events",
    orders:       "view_orders",
    participants: "view_orders",
    guestlist:    "edit_events",
    checkin:      "checkin",
    certificates: "edit_events",
    streaming:    "edit_events",
    team:         "manage_team",
    financial:    "view_financial",
    promoters:    null,
    messages:     "send_messages",
    coupons:      "manage_coupons",
    resales:      null,
    reviews:      null,
    schedule:     "edit_events",
  };

  const hasTabAccess = (tabKey: string): boolean => {
    if (isPlatformAdmin) return true;
    if (!isOrg || !myOrgRole) return true;
    const allowed = TAB_ORG_ROLES[tabKey];
    if (myCustomPerms && myCustomPerms.length > 0) {
      const reqPerm = PERMISSION_TAB_MAP[tabKey];
      if (reqPerm === null) {
        // Sem permissão dedicada: cai no controle por role
        return allowed === null || (Array.isArray(allowed) && allowed.includes(myOrgRole));
      }
      return myCustomPerms.includes(reqPerm);
    }
    return allowed === null || (Array.isArray(allowed) && allowed.includes(myOrgRole));
  };

  const canEditEvent = isPlatformAdmin
    ? true
    : isOrg
    ? (myCustomPerms && myCustomPerms.length > 0 ? myCustomPerms.includes("edit_events") : (myOrgRole ? canPostEvents(myOrgRole) : false))
    : true;
  const allOperations = EVENT_OPERATIONS.map((tab) => ({
    ...tab,
    locked: !hasTabAccess(tab.key),
  }));

  const getLockedToast = (tabKey: string) => {
    const needed = TAB_ORG_ROLES[tabKey];
    const neededLabel = needed
      ? needed.filter((r) => r !== "owner").map((r) => roleLabels[r as OrganizationRole]).join(` ${t("eventPanel.access.orSeparator")} `)
      : t("eventPanel.toast.allRoles");
    const currentLabel = myOrgRole ? (roleLabels[myOrgRole as OrganizationRole] || myOrgRole) : t("eventPanel.toast.unknownRole");
    toast({
      title: t("eventPanel.toast.accessRestrictedTitle"),
      description: t("eventPanel.toast.accessRestrictedDescription", { currentLabel, neededLabel }),
    });
  };

  useLayoutEffect(() => {
    scrollPageToTop();
  }, [location.pathname]);

  // Prefetch main event operation JS modules in background after mount
  useEffect(() => {
    const timer = setTimeout(() => {
      import("./ProducerEventDashboardTab");
      import("./ProducerEventTicketsTab");
      import("./ProducerEventParticipants");
      import("./ProducerEventOrders");
      import("./ProducerEventCheckin");
      import("./ProducerEventGuestlist");
      import("./ProducerEventFinancial");
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const handleTabHover = (tabKey: string) => {
    switch (tabKey) {
      case "dashboard":
        import("./ProducerEventDashboardTab");
        break;
      case "tickets":
        import("./ProducerEventTicketsTab");
        break;
      case "participants":
        import("./ProducerEventParticipants");
        break;
      case "orders":
        import("./ProducerEventOrders");
        break;
      case "checkin":
        import("./ProducerEventCheckin");
        break;
      case "guestlist":
        import("./ProducerEventGuestlist");
        break;
      case "financial":
        import("./ProducerEventFinancial");
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setIsFormDirty(!!detail?.isDirty);
    };
    window.addEventListener("producer-form-dirty", handler);
    return () => window.removeEventListener("producer-form-dirty", handler);
  }, []);

  const runIfClean = (action: () => void) => {
    // A navegação do evento é compartilhada pelos drawers do produtor e admin.
    // Fechar antes do guard também deixa a confirmação de alterações visível.
    if (isMobile) setOpenMobile(false);
    if (isFormDirty) {
      setPendingNavAction(() => action);
      setShowUnsavedDialog(true);
    } else {
      action();
    }
  };

  const navigateToTab = (tab: typeof EVENT_OPERATIONS[number]) => {
    runIfClean(() => {
      const params = new URLSearchParams(searchParams);
      params.delete("editorStep");
      const searchStr = params.toString();
      const suffix = searchStr ? `?${searchStr}` : "";
      const tabPath = isPtPanel ? (PT_TAB_PATH_MAP[tab.path] ?? tab.path) : tab.path;
      navigate(`${basePath}${tabPath}${suffix}`);
    });
  };


  const navigateToEditStep = (stepKey: (typeof EDIT_SECTIONS)[number]["key"]) => {
    runIfClean(() => {
      const params = new URLSearchParams(searchParams);
      params.set("editorStep", stepKey);
      setSearchParams(params);
    });
  };

  const closeInlineEditor = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("editorStep");
    setSearchParams(params);
  };

  const confirmSaveAndNavigate = () => {
    const onPersisted = () => {
      setShowUnsavedDialog(false);
      pendingNavAction?.();
      setPendingNavAction(null);
    };
    window.addEventListener("producer-form-persisted", onPersisted, { once: true });
    window.dispatchEvent(new CustomEvent("producer-form-persist-request"));
  };

  const confirmDiscardAndNavigate = () => {
    const onDiscarded = () => {
      setShowUnsavedDialog(false);
      pendingNavAction?.();
      setPendingNavAction(null);
    };
    window.addEventListener("producer-form-discarded", onDiscarded, { once: true });
    window.dispatchEvent(new CustomEvent("producer-form-discard-request"));
  };

  const openPublicPage = () => {
    if (eventPublicPath) {
      window.open(eventPublicPath, "_blank", "noopener,noreferrer");
    }
  };

  // Grupos de navegação do evento — reutilizados na barra de foco (portal) e no
  // fallback inline (caso o slot não exista).
  const eventGroups = (
    <>
      {/* EVENT GROUP */}
      {canEditEvent && <div>
        <h3 className="px-3 text-xs font-semibold text-muted-foreground mb-2">{t("eventPanel.nav.eventGroupTitle")}</h3>
        <div className="space-y-1">
          {EDIT_SECTIONS.map((item) => {
            const Icon = item.icon;
            const isActive = isInlineEditing && editorStep === item.key;
            return (
              <button
                key={item.key}
                onClick={() => navigateToEditStep(item.key as (typeof EDIT_SECTIONS)[number]["key"])}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors",
                  isActive
                    ? "border-primary/20 bg-primary/5 text-primary shadow-sm hover:bg-primary/10"
                    : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <p className="truncate font-medium text-sm">{t(item.labelKey)}</p>
              </button>
            );
          })}
        </div>
      </div>}

      {/* MANAGEMENT GROUP */}
      <div>
        <div className="px-3 mb-2 flex items-center justify-between">
          <h3 className="text-xs font-semibold text-muted-foreground">{t("eventPanel.nav.managementGroupTitle")}</h3>
          {isOrg && myOrgRole && (
            <span className="text-[10px] font-medium text-primary bg-primary/10 border border-primary/20 rounded-full px-2 py-0.5">
              {roleLabels[myOrgRole as OrganizationRole] || myOrgRole}
            </span>
          )}
        </div>
        <div className="space-y-1">
          {allOperations.map((item) => {
            const Icon = item.icon;
            const isActive = !isInlineEditing && activeTab.key === item.key;
            if (item.locked) {
              return (
                <button
                  key={item.key}
                  onClick={() => getLockedToast(item.key)}
                  className="flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-2 text-left text-muted-foreground/40 hover:text-muted-foreground/60 transition-colors"
                >
                  <Lock className="h-4 w-4 shrink-0" />
                  <p className="truncate font-medium text-sm">{t(item.labelKey)}</p>
                </button>
              );
            }
            return (
              <button
                key={item.key}
                onClick={() => navigateToTab(item as (typeof EVENT_OPERATIONS)[number])}
                onMouseEnter={() => handleTabHover(item.key)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl border px-3 py-2 text-left transition-colors",
                  isActive
                    ? "border-primary/20 bg-primary/5 text-primary shadow-sm hover:bg-primary/10"
                    : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <p className="truncate font-medium text-sm">{t(item.labelKey)}</p>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );

  const eventActions = (
    <>
      <Button
        size="sm"
        variant="outline"
        className="w-full justify-center gap-2 h-10 rounded-xl font-medium text-xs bg-background shadow-sm hover:shadow-md transition-all border-border/60"
        onClick={openPublicPage}
        disabled={!event?.slug}
      >
        <Globe className="h-4 w-4 text-primary" /> {event?.status === "published" ? t("eventPanel.actions.viewPublicPage") : t("eventPanel.actions.viewDraft")}
      </Button>
    </>
  );

  return (
    <div className="flex flex-col h-full" data-event-panel-root>
      {navSlot && createPortal(
        <div className="flex h-full flex-col gap-2 py-1">
          {/* Frameless, Weightless Event Header */}
          {event && (
            <div className="px-3 py-2 space-y-1 border-b border-border/40 pb-3">
              {/* Row 1: Back Link + Status Dot & External Link */}
              <div className="flex items-center justify-between text-xs">
                <Link
                  to={listPath}
                  onClick={() => { if (isMobile) setOpenMobile(false); }}
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="h-3 w-3" /> {t("eventPanel.nav.backToEvents")}
                </Link>

                <div className="flex items-center gap-2">
                  {(() => {
                    const statusStyle = getEventStatusStyle(event.status, event.visibility);
                    return (
                      <span className={cn("inline-flex items-center gap-1.5 text-[11px] font-medium", statusStyle.textColor)}>
                        <span className={cn("h-1.5 w-1.5 rounded-full animate-pulse", statusStyle.dotColor)} />
                        {statusStyle.label}
                      </span>
                    );
                  })()}
                  <button
                    onClick={openPublicPage}
                    disabled={!event.slug}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded hover:bg-muted/60 disabled:opacity-40"
                    title={event.status === "published" ? t("eventPanel.actions.viewPublicPage") : t("eventPanel.actions.viewDraft")}
                  >
                    <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* Row 2: Event Title (Clean 13px font) */}
              <h2 className="text-xs font-bold text-foreground leading-snug truncate" title={event.title}>
                {event.title}
              </h2>

              {/* Row 3: Minimal Single-Line Meta */}
              <p className="text-[11px] text-muted-foreground truncate">
                {event.start_date ? formatEventDateTimeRange(event.start_date, event.end_date, eventTimeZone, { dateStyle: "short" }) : ""}
                <span className="mx-1">•</span>
                {event.is_online ? t("eventPanel.header.online") : (event.venue_city || t("eventPanel.header.inPerson"))}
              </p>
            </div>
          )}

          {/* Navigation Groups (Gestão, Evento) */}
          <div className="min-h-0 flex-1 overflow-y-auto pr-1 custom-scrollbar space-y-5 px-1 pt-1">
            {eventGroups}
          </div>
        </div>,
        navSlot
      )}


      {/* Layout: Sidebar + Content */}
      <div className="mx-auto w-full max-w-6xl 2xl:max-w-7xl flex flex-1 gap-6 min-h-0 px-4 max-[1023px]:flex-col max-[1023px]:gap-4 max-[1023px]:px-0">
        {/* Tab Navigation - Sidebar (Desktop) — fallback inline quando não há barra de foco */}
        {!navSlot && (
          <div className="hidden lg:flex flex-col w-72 shrink-0 border-r border-border/70 pr-5 -ml-4 sticky top-6 self-start max-h-[calc(100vh-3rem)]">
            <Card className="border-border/70 flex flex-col overflow-hidden h-full rounded-2xl">
              <CardContent className="flex min-h-0 flex-1 flex-col gap-6 px-5 pt-5 pb-5">
                <div className="min-h-0 flex-1 overflow-y-auto pr-1 custom-scrollbar">
                  <div className="space-y-6">
                    {eventGroups}
                  </div>
                </div>
              </CardContent>
              <div className="p-5 pt-0 mt-auto border-t border-border/50 bg-muted/5">
                <div className="pt-4 space-y-2">
                  {eventActions}
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Tab Content - Main Area */}
        <div 
          className="flex-1 min-w-0 overflow-y-auto pr-2 lg:pr-4 max-[1023px]:overflow-visible max-[1023px]:pr-0 pt-1"          data-scroll-container
        >
          {event?.status === "draft" && (
            <div className="mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-in fade-in slide-in-from-top-3 duration-200">
              <div className="flex gap-3 items-start">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-amber-800 dark:text-amber-400">{t("eventPanel.draftBanner.title")}</p>
                  <p className="text-xs text-amber-700 dark:text-amber-500 leading-relaxed">
                    {t("eventPanel.draftBanner.description")}
                  </p>
                </div>
              </div>
              <Button 
                size="sm" 
                onClick={() => setIsPublishConfirmOpen(true)}
                disabled={publishMutation.isPending}
                className="bg-green-600 hover:bg-green-700 text-white font-semibold shrink-0 rounded-xl shadow-sm hover:shadow transition-all"
              >
                {t("eventPanel.draftBanner.publishNow")}
              </Button>
            </div>
          )}

          {isInlineEditing ? (
            <ProducerEventForm onCancel={closeInlineEditor} returnPath={listPath} />
          ) : !hasTabAccess(activeTab.key) ? (
            <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 text-center px-4">
              <div className="h-14 w-14 rounded-full bg-muted/60 flex items-center justify-center">
                <Lock className="h-7 w-7 text-muted-foreground" />
              </div>
              <div className="max-w-sm">
                <p className="text-base font-semibold">{t("eventPanel.access.title")}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  <Trans
                    t={t}
                    i18nKey="eventPanel.access.roleLine"
                    values={{ role: roleLabels[myOrgRole as OrganizationRole] || myOrgRole }}
                  >
                    Seu cargo é <strong>{{ role: roleLabels[myOrgRole as OrganizationRole] || myOrgRole } as any}</strong>.
                  </Trans>
                  {(() => {
                    const needed = TAB_ORG_ROLES[activeTab.key];
                    if (!needed) return null;
                    const labels = needed.filter((r) => r !== "owner").map((r) => roleLabels[r as OrganizationRole]).join(` ${t("eventPanel.access.orSeparator")} `);
                    return ` ${t("eventPanel.access.requiresTab", { labels })}`;
                  })()}
                </p>
                <p className="text-xs text-muted-foreground/70 mt-2">{t("eventPanel.access.contactAdmin")}</p>
              </div>
            </div>
          ) : (
            <Suspense fallback={
              <div className="flex items-center justify-center min-h-[30vh]">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            }>
              <Outlet />
            </Suspense>
          )}
        </div>
      </div>

      {/* Unsaved changes guard dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent className="rounded-2xl max-w-[90vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("eventPanel.unsavedDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("eventPanel.unsavedDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <AlertDialogAction onClick={confirmSaveAndNavigate} className="w-full mt-0 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl">
              {t("eventPanel.unsavedDialog.save")}
            </AlertDialogAction>
            <AlertDialogAction onClick={confirmDiscardAndNavigate} className="w-full mt-0 border border-destructive/30 bg-destructive/5 text-destructive hover:bg-destructive/10 font-medium rounded-xl">
              {t("eventPanel.unsavedDialog.discard")}
            </AlertDialogAction>
            <AlertDialogCancel onClick={() => { setShowUnsavedDialog(false); setPendingNavAction(null); }} className="w-full mt-0 rounded-xl border-border">
              {t("eventPanel.unsavedDialog.continueEditing")}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Publish confirmation dialog */}
      <AlertDialog open={isPublishConfirmOpen} onOpenChange={setIsPublishConfirmOpen}>
        <AlertDialogContent className="rounded-2xl max-w-[90vw] sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("eventPanel.publishDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("eventPanel.publishDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="rounded-xl">{t("eventPanel.publishDialog.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setIsPublishConfirmOpen(false); publishMutation.mutate(); }} className="rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold">
              {t("eventPanel.publishDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reviews feature announcement (first-time onboarding) */}
      {basePath && (
        <ReviewsFeatureAnnouncement
          open={showReviewsAnnouncement}
          onOpenChange={dismissReviewsAnnouncement}
          eventId={id!}
          basePath={basePath}
        />
      )}
    </div>
  );
}
